/**
 * Blog post: How long does it take to prepare for German B1, B2, C1.
 * SEO target: "how long to prepare for german b2 exam", "how many hours
 * german b1 b2 c1", "german exam preparation time". GSC already shows the
 * site surfacing for "how to pass b2 german exam" and "german b2 exam
 * preparation"; this page answers the time-planning intent behind them.
 * Cross-provider topic, so no RelatedResources; manual internal links.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function PrepTimePost() {
  const lang = useLangParam();

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        How long you need for a German exam depends on two numbers: the distance between your current
        level and the target level, and the hours per week you can realistically study. This guide gives
        you honest benchmarks for both, plus the one phase almost everyone underestimates: exam-specific
        preparation after you have reached the level.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The hour benchmarks
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Total guided learning time from zero, based on common CEFR estimates used by major providers:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space text-3xl font-black text-black mb-1">350–650</p>
          <p className="font-space text-sm font-bold text-black/60 uppercase tracking-wide mb-2">hours to B1</p>
          <p className="text-sm text-black/60">Independent everyday communication</p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space text-3xl font-black text-black mb-1">600–800</p>
          <p className="font-space text-sm font-bold text-black/60 uppercase tracking-wide mb-2">hours to B2</p>
          <p className="text-sm text-black/60">Work and study readiness</p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] text-center">
          <p className="font-space text-3xl font-black text-black mb-1">800–1,000</p>
          <p className="font-space text-sm font-bold text-black/60 uppercase tracking-wide mb-2">hours to C1</p>
          <p className="text-sm text-black/60">University and professional fluency</p>
        </div>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Step distances matter more if you already speak German: roughly <strong>200 to 300 hours from B1
        to B2</strong>, and another <strong>200 to 300 hours from B2 to C1</strong>. At 10 focused hours a
        week, each step is five to seven months; at 20 hours a week, around three.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The phase people forget: exam preparation is not language learning
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Reaching B2 and passing a B2 exam are two different projects. Once your German is at the level,
        plan <strong>4 to 8 weeks of exam-specific training</strong>: learning the task formats, drilling
        the timing, and getting feedback on writing and speaking. Candidates regularly fail exams above
        their true level for format reasons, and pass exams at their level after two weeks of format
        drilling. The exam rewards people who know the exam.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">The 8-week exam plan in short</h3>
        <ol className="list-decimal pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>Weeks 1 and 2: one full mock exam for a baseline, then study the task formats of every section</li>
          <li>Weeks 3 to 6: targeted training on your weakest sections, one timed mock per week, one scored writing task every second day</li>
          <li>Weeks 7 and 8: full simulations only, under strict exam conditions, until you hit the readiness margin (70 to 75 percent, three times in a row)</li>
        </ol>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How to find out where you actually stand
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Skip the guesswork: a full timed mock exam is the most accurate self-assessment there is, because
        it measures you against the real pass threshold of 60 percent per section. You can take a
        {' '}<Link to={`/${lang}/try`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">free mock exam without an account</Link>{' '}
        for telc, Goethe, or ÖSD at B1 to C1. Your section scores tell you immediately whether you are in
        the exam-preparation phase or still in the language-learning phase, and the
        {' '}<Link to={`/${lang}/mock-exams`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">per-exam overview pages</Link>{' '}
        show exactly what each exam demands.
      </p>
    </article>
  );
}
